/**
 * El historial fiscal de un consultorio y la vista de cada factura.
 *
 * Es la respuesta a «¿me manda las facturas de estos meses?», que hoy se
 * resuelve buscando archivos a mano. Cada fila muestra el **mes que la factura
 * cubre** —no su fecha de emisión, que en la práctica va en tandas de puesta
 * al día— y desde ahí se abre el documento o se baja el XML.
 *
 * Dos formas de llevarse la factura, a propósito:
 *   · la vista, que se imprime o se guarda como PDF y es la que se le manda al
 *     cliente;
 *   · el XML, que es el comprobante legal ante Hacienda y el que pide quien
 *     lleva la contabilidad.
 *
 * El documento en sí —y su visor— viven en `components/common/InvoiceDocument`:
 * el cliente ve exactamente el mismo render desde su cobro en el portal, y de
 * un comprobante fiscal no puede haber dos dibujos que puedan divergir.
 */

import { Fragment, useState, type DragEvent } from 'react';
import { cedulaLabel, dateLabel, periodLabel, periodLabelShort, today } from '@cleanblue/shared';
import { api } from '@/lib/api';
import { useApi, useApiMutation } from '@/lib/hooks';
import { colonDigits, colonDigitsToMinor, formatColonDigits, minorToColonDigits } from '@/lib/paymentAmount';
import { Icon } from '@/components/common/NavIcons';
import {
  InvoiceViewer, isCreditNote, type InvoiceDetail,
} from '@/components/common/InvoiceDocument';
import { ManualPaymentModal, type ManualPaymentTarget } from './Payments';
import { Async, Badge, Button, Card, Field, Modal, Money, Note, Table, TableSkeleton } from '@/components/ui';

export interface InvoiceRow {
  id: string;
  document_number: string;
  document_type: string | null;
  issued_on: string;
  receptor_name: string | null;
  receptor_tax_id: string | null;
  taxable: string;
  tax: string;
  total: string;
  periods: string[];
  period_source: string | null;
  /* El PDF conserva la presentación original de GTI. El XML es el comprobante
     firmado y permite reconstruir la vista cuando no hay PDF. */
  has_xml: boolean;
  has_pdf: boolean;
  source_note: string | null;
}

interface InvoiceYearDocument {
  id: string;
  document_number: string;
  document_type: string | null;
  issued_on: string;
  receptor_name: string | null;
  receptor_tax_id: string | null;
  total_minor: number;
  periods: string[];
  charge_payer_id: string | null;
  annuls_document_number: string | null;
  has_pdf: boolean;
  has_xml: boolean;
  presentation: 'ORIGINAL_PDF' | 'XML_RENDER' | 'METADATA_RENDER' | 'UNAVAILABLE';
  missing: string[];
  compliance: 'METADATA_ONLY' | 'PDF_ONLY' | 'XML_ONLY' | 'COMPLETE' | 'REVIEW';
  review_reason: string | null;
  shared_period_count: number;
  association_status: 'LINKED' | 'UNASSIGNED' | 'AMBIGUOUS' | 'CONFLICT';
  association_reason: string | null;
  candidate_charge_payer_ids: string[];
}

interface InvoiceYearPayer {
  charge_payer_id: string;
  payer_name: string;
  billing_name: string | null;
  billing_tax_id: string | null;
  due_minor: number;
  paid_minor: number;
  balance_minor: number;
  payment_status: string;
  invoice_status: string;
  invoices: InvoiceYearDocument[];
}

interface ManualInvoicePreview {
  analysis: {
    document_number: string; document_type: string; issued_on: string;
    fiscal_key: string | null; receptor_name: string | null; receptor_tax_id: string | null;
    total_minor: number; currency: string; signature_present: boolean | null;
    pdf_readable: boolean | null; review_reasons: string[]; is_credit_note: boolean;
  };
  periods: string[];
  expected: {
    unit_code: string; payer_name: string; billing_name: string | null;
    billing_tax_id: string | null; total_minor: number; period: string;
    payment_status: string; paid_minor: number;
  };
}

interface InvoiceYearMonth {
  period: string;
  applies: boolean;
  is_exception: boolean;
  exception_reason: string | null;
  due_minor: number;
  paid_minor: number;
  balance_minor: number;
  payment_status: 'NO_CHARGE' | 'PAID' | 'PARTIAL' | 'PENDING';
  document_status: 'MISSING' | 'METADATA_ONLY' | 'PDF_ONLY' | 'XML_ONLY' | 'COMPLETE' | 'REVIEW';
  association_status: 'NONE' | 'LINKED' | 'UNASSIGNED' | 'AMBIGUOUS' | 'CONFLICT';
  association_reason: string | null;
  reported_invoiced_without_document: boolean;
  payers: InvoiceYearPayer[];
  unassigned_invoices: InvoiceYearDocument[];
}

interface InvoiceYearResponse {
  year: number;
  years: number[];
  unit_code: string;
  current_period: string;
  service_start: string | null;
  service_end: string | null;
  summary: {
    due_minor: number;
    paid_minor: number;
    balance_minor: number;
    pending_months: number;
    missing_invoice_months: number;
    incomplete_documents: number;
    complete_months: number;
  };
  months: InvoiceYearMonth[];
  unassigned: InvoiceYearDocument[];
  history: InvoiceYearDocument[];
}

type InvoiceViewTarget = Pick<InvoiceYearDocument,
  'id' | 'document_number' | 'document_type' | 'receptor_name' | 'has_xml' | 'has_pdf'>;


/** Los meses que cubre, legibles: «oct 25» o «mar 26 → jun 26 (4 meses)». */
function periodsLabel(periods: string[]): string {
  if (periods.length === 0) return '—';
  if (periods.length === 1) return periodLabelShort(periods[0]!);
  const sorted = [...periods].sort();
  if (sorted.length <= 3) return sorted.map(periodLabelShort).join(', ');
  return `${periodLabelShort(sorted[0]!)} → ${periodLabelShort(sorted[sorted.length - 1]!)} (${sorted.length} meses)`;
}

/** En esta vista el año ya vive en el selector: la fila solo dice “Setiembre”. */
function monthOnlyLabel(period: string): string {
  const month = periodLabel(period).replace(/\s+\d{4}$/, '');
  return month.charAt(0).toUpperCase() + month.slice(1);
}

const PAYMENT_LABEL: Record<InvoiceYearMonth['payment_status'], string> = {
  NO_CHARGE: 'Sin cobro', PAID: 'Pagado', PARTIAL: 'Pago parcial', PENDING: 'Pendiente',
};

function monthDocuments(month: InvoiceYearMonth): InvoiceYearDocument[] {
  const unique = new Map<string, InvoiceYearDocument>();
  month.payers.flatMap((payer) => payer.invoices)
    .concat(month.unassigned_invoices)
    .forEach((invoice) => unique.set(invoice.id, invoice));
  return [...unique.values()];
}

function shortDocument(invoice: InvoiceYearDocument): string {
  const tail = invoice.document_number.slice(-4).replace(/^0+/, '') || '0';
  return `${isCreditNote(invoice.document_type) ? 'NC' : 'FE'} ${tail}`;
}

/**
 * La tabla de comprobantes, sola — para poder montarla también fuera del
 * detalle del consultorio (la galería de `Cobros → Facturación`, CB-14) sin
 * reimplementarla. Los tres puntos de extensión (`periodExtra`, `docExtra`,
 * `receptorExtra`) existen para que la galería anote lo suyo —factura
 * compartida entre meses, anulaciones, anomalía de receptor— sin que esta
 * tabla sepa nada de esas reglas.
 */
export function InvoiceTable({ invoices, onView, periodExtra, docExtra, receptorExtra }: {
  invoices: InvoiceRow[];
  onView: (inv: InvoiceRow) => void;
  periodExtra?: (inv: InvoiceRow) => React.ReactNode;
  docExtra?: (inv: InvoiceRow) => React.ReactNode;
  receptorExtra?: (inv: InvoiceRow) => React.ReactNode;
}) {
  return (
    <Table head={
      /* El orden es el de la importancia en el teléfono, donde la tabla
         scrollea de lado: mes, MONTO y las acciones entran en la primera
         pantalla; el número de documento y el receptor —que igual se ven
         enteros al abrir la factura— quedan al final del scroll (pedido del
         dueño, 27-ago-2026). */
      <tr>
        <th className="tbl--anchor">Mes que cubre</th>
        <th className="right">Total</th><th />
        {/* Documento y Receptor: ya son las últimas columnas del scroll en el
            teléfono (pedido del dueño, 27-ago-2026) — bajo 640px directamente
            no hace falta arrastrar para llegar a ellas, se ven al abrir la
            factura. */}
        <th className="col-hide-sm">Documento</th><th className="col-hide-sm">Receptor</th>
      </tr>
    }>
      {invoices.map((inv) => (
        <tr key={inv.id}>
          <td className="strong tbl--anchor">
            {periodsLabel(inv.periods)}
            {/* La fecha de emisión va como dato secundario: lo que
                importa es qué mes se está cobrando. */}
            <div className="tbl__sub">emitida {dateLabel(inv.issued_on)}</div>
            {periodExtra?.(inv)}
          </td>
          <td className="right">
            <Money value={inv.total} />
            {/* La nota de crédito se marca junto al monto en negativo, que es
                donde el ojo la va a buscar ahora que el documento va al final. */}
            {isCreditNote(inv.document_type) && (
              <div><Badge tone="warn" plain>Nota de crédito</Badge></div>
            )}
          </td>
          <td>
            <div className="row" style={{ gap: 6 }}>
              {/* El «elemento gráfico de archivo» que pidió el dueño: el
                  documento se abre desde acá. */}
              <Button onClick={() => onView(inv)} title="Abrir el comprobante">
                <Icon name="documento" /> Ver
              </Button>
              {inv.has_xml && (
                <a
                  className="btn"
                  href={`/api/admin/invoices/${inv.id}/xml`}
                  title="El comprobante electrónico legal, para contabilidad"
                >
                  XML
                </a>
              )}
            </div>
          </td>
          <td className="small mono col-hide-sm">
            {inv.document_number}
            {docExtra?.(inv)}
          </td>
          <td className="small col-hide-sm">
            {inv.receptor_name ?? '—'}
            {/* La fila no trae el tipo de identificación; `cedulaLabel` agrupa
                igual las dos formas que no son ambiguas y deja el resto crudo. */}
            {inv.receptor_tax_id && <div className="tbl__sub mono cedula">{cedulaLabel(inv.receptor_tax_id)}</div>}
            {receptorExtra?.(inv)}
          </td>
        </tr>
      ))}
    </Table>
  );
}

export function InvoiceHistoryCard({ unitId }: { unitId: string }) {
  const [year, setYear] = useState<number | null>(null);
  const query = useApi<InvoiceYearResponse>(
    `/admin/units/${unitId}/invoice-year${year ? `?year=${year}` : ''}`,
    { placeholderData: (previous) => previous },
  );
  const [viewing, setViewing] = useState<InvoiceViewTarget | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [paying, setPaying] = useState<ManualPaymentTarget | null>(null);
  const [invoicing, setInvoicing] = useState<{
    month: InvoiceYearMonth; invoice: InvoiceYearDocument | null;
  } | null>(null);
  const [linking, setLinking] = useState<{
    month: InvoiceYearMonth; invoice: InvoiceYearDocument;
  } | null>(null);

  const paymentTone = (status: InvoiceYearMonth['payment_status']) => (
    status === 'PAID' ? 'ok' : status === 'PENDING' || status === 'PARTIAL' ? 'warn' : 'neutral'
  ) as 'ok' | 'warn' | 'neutral';

  const DocumentActions = ({ invoice }: { invoice: InvoiceYearDocument }) => (
    <div className="row invoice-year__actions">
      {invoice.presentation !== 'UNAVAILABLE' ? (
        <Button onClick={() => setViewing(invoice)} title="Abrir la mejor vista disponible">
          <Icon name="documento" /> {invoice.compliance === 'METADATA_ONLY' ? 'Ver datos' : 'Ver'}
        </Button>
      ) : (
        <span className="small muted" title={`Falta: ${invoice.missing.join(', ')}`}>
          Vista incompleta
        </span>
      )}
      {invoice.has_xml && (
        <a className="btn" href={`/api/admin/invoices/${invoice.id}/xml`}>XML</a>
      )}
    </div>
  );

  const openPayment = (payer: InvoiceYearPayer) => setPaying({
    id: payer.charge_payer_id,
    billing_name_snapshot: payer.billing_name ?? payer.payer_name,
    due_minor: payer.due_minor,
    paid_minor: payer.paid_minor,
  });

  const DocumentSummary = ({ month }: { month: InvoiceYearMonth }) => {
    const documents = monthDocuments(month);
    if (month.association_status === 'CONFLICT') return (
      <span>Datos fiscales en conflicto<span className="tbl__sub">{month.association_reason}</span></span>
    );
    if (month.association_status === 'AMBIGUOUS') return (
      <span>Factura existente · elegir responsable<span className="tbl__sub">{month.association_reason}</span></span>
    );
    if (month.association_status === 'UNASSIGNED') return (
      <span>Factura existente · falta vincular responsable<span className="tbl__sub">{month.association_reason}</span></span>
    );
    if (month.reported_invoiced_without_document) return (
      <span>Facturado · comprobante no identificado<span className="tbl__sub">Confirmación histórica auditable</span></span>
    );
    const label = {
      MISSING: 'Sin factura',
      PDF_ONLY: 'Solo PDF · falta XML',
      XML_ONLY: 'XML disponible · PDF generado',
      COMPLETE: 'PDF y XML',
      REVIEW: 'Documento con observaciones',
      METADATA_ONLY: 'Solo datos · sin originales',
    }[month.document_status];
    if (documents.length === 0) return <span className="muted">{label}</span>;
    if (documents.length > 1) return <span>{label}<span className="tbl__sub">{documents.length} documentos</span></span>;
    const invoice = documents[0]!;
    return (
      <span>
        {label}
        <span className="tbl__sub">{shortDocument(invoice)}</span>
        {invoice.shared_period_count > 1 && <span className="tbl__sub">Compartida · {invoice.shared_period_count} meses</span>}
        {invoice.review_reason && <span className="tbl__sub">{invoice.review_reason}</span>}
      </span>
    );
  };

  const PayerSummary = ({ month }: { month: InvoiceYearMonth }) => {
    if (month.payers.length === 0) return <span className="muted">Sin responsable</span>;
    if (month.payers.length > 1) return <span>{month.payers.length} pagadores</span>;
    const payer = month.payers[0]!;
    return (
      <span>
        {payer.payer_name}
        {payer.billing_name && payer.billing_name !== payer.payer_name && (
          <span className="tbl__sub">Se factura a {payer.billing_name}</span>
        )}
      </span>
    );
  };

  const MonthDetail = ({ month }: { month: InvoiceYearMonth }) => (
    <div className="invoice-month-detail">
      {month.is_exception && (
        <div className="invoice-month-detail__exception">
          <Badge tone="warn">Por revisar</Badge>
          <span>{month.exception_reason}</span>
        </div>
      )}
      {month.payers.length === 0 ? (
        <p className="small muted">Este mes aplica, pero no tiene un cobro generado.</p>
      ) : month.payers.map((payer) => (
        <div key={payer.charge_payer_id} className="invoice-month-detail__payer">
          <div>
            <strong>{payer.payer_name}</strong>
            {payer.billing_name && payer.billing_name !== payer.payer_name && (
              <span className="tbl__sub">Se factura a {payer.billing_name}</span>
            )}
          </div>
          <div className="invoice-month-detail__money">
            <span>Debe <Money value={payer.due_minor} /></span>
            <span>Pagado <Money value={payer.paid_minor} /></span>
            <span>Saldo <Money value={payer.balance_minor} /></span>
          </div>
          <div className="invoice-month-detail__docs">
            {payer.invoices.length === 0 ? (
              <span className="small muted">
                {payer.invoice_status === 'ISSUED' ? 'Facturado · comprobante no identificado' : 'Sin factura'}
              </span>
            )
              : payer.invoices.map((invoice) => (
                <div key={invoice.id} className="invoice-year__document">
                  <div>
                    <span className="mono small">{invoice.document_number}</span>
                    <span className="small muted"> · {invoice.receptor_name ?? 'Sin receptor'}</span>
                    {invoice.shared_period_count > 1 && (
                      <Badge tone="info" plain>Documento compartido entre meses</Badge>
                    )}
                  </div>
                  <DocumentActions invoice={invoice} />
                </div>
              ))}
          </div>
          {(payer.payment_status === 'PENDING' || payer.payment_status === 'PARTIAL') && (
            <Button variant="primary" onClick={() => openPayment(payer)}>Registrar pago</Button>
          )}
        </div>
      ))}
      {month.unassigned_invoices.map((invoice) => (
        <div key={invoice.id} className="invoice-month-detail__payer invoice-payer--unassigned">
          <div>
            <strong>{invoice.association_status === 'CONFLICT'
              ? 'Datos fiscales en conflicto' : 'Factura existente pendiente de vinculación'}</strong>
            <span className="tbl__sub">{invoice.association_reason}</span>
            <span className="tbl__sub">{invoice.receptor_name} · {invoice.receptor_tax_id ?? 'sin identificación'}</span>
          </div>
          <div className="mono small">{invoice.document_number} · <Money value={invoice.total_minor} /></div>
          <div className="row">
            <DocumentActions invoice={invoice} />
            {invoice.association_status !== 'CONFLICT' && (
              <Button variant="primary" onClick={() => setLinking({ month, invoice })}>Vincular factura</Button>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  const MonthAction = ({ month }: { month: InvoiceYearMonth }) => {
    const onlyPayer = month.payers.length === 1 ? month.payers[0]! : null;
    const canPay = onlyPayer
      && (month.payment_status === 'PENDING' || month.payment_status === 'PARTIAL');
    const documents = monthDocuments(month);
    const unassigned = month.unassigned_invoices[0] ?? null;
    const incomplete = month.payers.flatMap((payer) => payer.invoices)
      .find((invoice) => invoice.compliance !== 'COMPLETE');
    const missingDocument = documents.length === 0 && month.payers.length > 0
      && !month.reported_invoiced_without_document;
    const fileAction = incomplete?.compliance === 'PDF_ONLY' ? 'Añadir XML'
      : incomplete?.compliance === 'XML_ONLY' ? 'Añadir PDF'
        : incomplete?.compliance === 'METADATA_ONLY' ? 'Completar archivos'
          : null;
    const monthName = `${monthOnlyLabel(month.period)} de ${month.period.slice(0, 4)}`;
    const action = (label: string, icon: Parameters<typeof Icon>[0]['name'], onClick: () => void,
      tone: 'primary' | 'warning' | 'neutral' = 'neutral', expandedState?: boolean) => (
      <button
        type="button"
        className={`btn invoice-action-btn${tone === 'primary' ? ' btn--primary' : ''}${tone === 'warning' ? ' invoice-action-btn--warning' : ''}`}
        aria-label={label}
        aria-expanded={expandedState}
        title={label}
        data-tooltip={label}
        onClick={onClick}
      >
        <Icon name={icon} size={18} />
      </button>
    );
    const documentAction = unassigned && unassigned.association_status !== 'CONFLICT'
      ? action(`Vincular ${shortDocument(unassigned)}`, 'vincular', () => setLinking({ month, invoice: unassigned }))
      : unassigned?.association_status === 'CONFLICT'
        ? action(`Revisar conflicto de ${monthName}`, 'alerta', () => setExpanded(month.period), 'warning')
        : missingDocument
          ? action(`Registrar factura de ${monthName}`, 'documento-mas', () => setInvoicing({ month, invoice: null }))
          : incomplete && fileAction
            ? action(`${fileAction} a ${shortDocument(incomplete)}`,
              incomplete.compliance === 'PDF_ONLY' ? 'xml-mas' : 'pdf-mas',
              () => setInvoicing({ month, invoice: incomplete }))
            : incomplete?.compliance === 'REVIEW'
              ? action(`Revisar conflicto de ${monthName}`, 'alerta', () => setExpanded(month.period), 'warning')
              : null;
    const isExpanded = expanded === month.period;
    return (
      <div className="invoice-year__row-actions" aria-label={`Acciones de ${monthName}`}>
        <span className="invoice-action-slot" aria-hidden={!canPay || undefined}>
          {canPay && action(`Registrar pago de ${monthName}`, 'pago-mas', () => openPayment(onlyPayer), 'primary')}
        </span>
        <span className="invoice-action-slot" aria-hidden={!documentAction || undefined}>{documentAction}</span>
        <span className="invoice-action-slot">
          {action(`${isExpanded ? 'Ocultar' : 'Ver'} detalle de ${monthName}`,
            isExpanded ? 'ojo-cerrado' : 'ojo',
            () => setExpanded(isExpanded ? null : month.period), 'neutral', isExpanded)}
        </span>
      </div>
    );
  };

  return (
    <>
      <Card title="Estado mensual">
        <Async query={query} skeleton={<TableSkeleton rows={8} cols={4} />}>
          {(data) => (
            <div className={`invoice-year${query.isFetching ? ' is-loading' : ''}`} aria-busy={query.isFetching}>
              <div className="invoice-year__toolbar">
                <div
                  className="segmented invoice-year__years"
                  role="group"
                  aria-label="Año del estado mensual"
                  style={{
                    '--seg-n': data.years.length,
                    '--seg-i': data.years.indexOf(data.year),
                  } as React.CSSProperties}
                >
                  <div className="segmented__pill" />
                  {data.years.map((option) => (
                    <button key={option} type="button" aria-pressed={option === data.year}
                      className={`segmented__opt${option === data.year ? ' is-active' : ''}`}
                      onClick={() => setYear(option)}>{option}</button>
                  ))}
                </div>
                <div className="invoice-year__activity small muted">
                  {data.service_start
                    ? `Servicio activo desde ${periodLabel(data.service_start)}`
                    : 'Sin fecha de inicio confirmada'}
                  {data.service_end ? ` hasta ${periodLabel(data.service_end)}` : ''}
                  {query.isFetching && <span role="status"> · Actualizando…</span>}
                </div>
              </div>

              <div className="invoice-year__metrics" aria-label={`Resumen de ${data.year}`}>
                <div><span>Debe</span><strong><Money value={data.summary.due_minor} /></strong></div>
                <div><span>Pagado</span><strong><Money value={data.summary.paid_minor} /></strong></div>
                <div><span>Saldo</span><strong><Money value={data.summary.balance_minor} /></strong></div>
                <div><span>Meses con saldo</span><strong>{data.summary.pending_months}</strong></div>
                <div><span>Sin factura</span><strong>{data.summary.missing_invoice_months}</strong></div>
                <div><span>Docs incompletos</span><strong>{data.summary.incomplete_documents}</strong></div>
                <div><span>Meses completos</span><strong>{data.summary.complete_months}</strong></div>
              </div>

              {data.months.length === 0 ? (
                <div className="invoice-year__empty">
                  <strong>Sin meses para mostrar en {data.year}</strong>
                  <span>No hay vigencia ni evidencia financiera asociada a este año.</span>
                </div>
              ) : (
                <>
                  <div className="invoice-year__desktop">
                    <table className="invoice-year__table">
                      <thead><tr>
                        <th>Mes</th><th>Estado</th><th className="right">Debe</th>
                        <th className="right">Pagado</th><th className="right">Saldo</th>
                        <th>Responsable</th><th>Documento</th><th>Acción</th>
                      </tr></thead>
                      <tbody>
                        {data.months.map((month) => (
                          <Fragment key={month.period}>
                            <tr className={month.is_exception ? 'is-exception' : undefined}>
                              <td>
                                <strong aria-label={`${monthOnlyLabel(month.period)} de ${data.year}`}>
                                  {monthOnlyLabel(month.period)}
                                </strong>
                                {month.is_exception && <span className="tbl__sub">Excepción</span>}
                              </td>
                              <td><Badge tone={paymentTone(month.payment_status)}>{PAYMENT_LABEL[month.payment_status]}</Badge></td>
                              <td className="right"><Money value={month.due_minor} /></td>
                              <td className="right"><Money value={month.paid_minor} /></td>
                              <td className="right strong"><Money value={month.balance_minor} /></td>
                              <td><PayerSummary month={month} /></td>
                              <td><DocumentSummary month={month} /></td>
                              <td><MonthAction month={month} /></td>
                            </tr>
                            {expanded === month.period && (
                              <tr key={`${month.period}-detail`} className="invoice-year__detail-row">
                                <td colSpan={8}><MonthDetail month={month} /></td>
                              </tr>
                            )}
                          </Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="invoice-year__mobile">
                    {data.months.map((month) => (
                      <section key={month.period} className={`invoice-month-card${month.is_exception ? ' is-exception' : ''}`}>
                        <header>
                          <strong aria-label={`${monthOnlyLabel(month.period)} de ${data.year}`}>
                            {monthOnlyLabel(month.period)}
                          </strong>
                          <Badge tone={paymentTone(month.payment_status)}>{PAYMENT_LABEL[month.payment_status]}</Badge>
                        </header>
                        <dl>
                          <div><dt>Debe</dt><dd><Money value={month.due_minor} /></dd></div>
                          <div><dt>Saldo</dt><dd><Money value={month.balance_minor} /></dd></div>
                          <div><dt>Pagado</dt><dd><Money value={month.paid_minor} /></dd></div>
                          <div><dt>Responsable</dt><dd><PayerSummary month={month} /></dd></div>
                          <div><dt>Documento</dt><dd><DocumentSummary month={month} /></dd></div>
                        </dl>
                        <MonthAction month={month} />
                        {expanded === month.period && <MonthDetail month={month} />}
                      </section>
                    ))}
                  </div>
                </>
              )}

              {data.unassigned.length > 0 && (
                <details className="invoice-year__history plegable">
                  <summary className="plegable__cabeza">Mes por asignar ({data.unassigned.length})</summary>
                  {data.unassigned.map((invoice) => (
                    <div key={invoice.id} className="invoice-year__history-row">
                      <span>{invoice.document_number} · {invoice.receptor_name ?? 'Sin receptor'}</span>
                      <DocumentActions invoice={invoice} />
                    </div>
                  ))}
                </details>
              )}

              {data.history.length > 0 && (
                <details className="invoice-year__history plegable">
                  <summary className="plegable__cabeza">Historial y anulaciones ({data.history.length})</summary>
                  {data.history.map((invoice) => (
                    <div key={invoice.id} className="invoice-year__history-row">
                      <span>{invoice.document_number} · {invoice.receptor_name ?? 'Sin receptor'}</span>
                      <DocumentActions invoice={invoice} />
                    </div>
                  ))}
                </details>
              )}
            </div>
          )}
        </Async>
      </Card>

      {viewing && <InvoiceModal invoice={viewing} onClose={() => setViewing(null)} />}
      {paying && <ManualPaymentModal receivable={paying} onClose={() => setPaying(null)} />}
      {invoicing && (
        <ManualInvoiceModal
          unitId={unitId}
          unitCode={query.data?.unit_code ?? ''}
          month={invoicing.month}
          invoice={invoicing.invoice}
          onClose={() => setInvoicing(null)}
        />
      )}
      {linking && (
        <LinkInvoiceModal
          unitId={unitId}
          month={linking.month}
          invoice={linking.invoice}
          onClose={() => setLinking(null)}
        />
      )}
    </>
  );
}

/** Asigna una factura que ya existe; nunca pide ni vuelve a subir archivos. */
function LinkInvoiceModal({ unitId, month, invoice, onClose }: {
  unitId: string; month: InvoiceYearMonth; invoice: InvoiceYearDocument; onClose: () => void;
}) {
  const candidates = month.payers.filter((payer) =>
    invoice.candidate_charge_payer_ids.length === 0
      || invoice.candidate_charge_payer_ids.includes(payer.charge_payer_id));
  const [payerId, setPayerId] = useState(candidates.length === 1 ? candidates[0]!.charge_payer_id : '');
  const mutation = useApiMutation(
    () => api.post(`/admin/manual-invoices/${invoice.id}/link`, { charge_payer_id: payerId }),
    [`/admin/units/${unitId}/invoice-year`, `/admin/units/${unitId}`, '/admin/invoices'],
    { onSuccess: onClose },
  );
  return (
    <Modal title={`Vincular factura · ${shortDocument(invoice)}`} onClose={onClose}>
      <div className="stack">
        <Note tone="info">La factura y sus archivos ya existen. Esta acción solo asigna el responsable; no modifica el pago.</Note>
        <dl className="invoice-preview__facts">
          <dt>Documento</dt><dd>{invoice.document_number}</dd>
          <dt>Receptor</dt><dd>{invoice.receptor_name ?? '—'} · {invoice.receptor_tax_id ?? 'sin identificación'}</dd>
          <dt>Total</dt><dd><Money value={invoice.total_minor} /></dd>
          <dt>Meses</dt><dd>{invoice.periods.map(periodLabelShort).join(', ')}</dd>
          <dt>Archivos</dt><dd>{invoice.has_pdf ? 'PDF' : ''}{invoice.has_pdf && invoice.has_xml ? ' + ' : ''}{invoice.has_xml ? 'XML' : ''}</dd>
        </dl>
        <Field label="Responsable" required>
          <select value={payerId} onChange={(event) => setPayerId(event.target.value)}>
            <option value="">Seleccioná</option>
            {candidates.map((payer) => (
              <option key={payer.charge_payer_id} value={payer.charge_payer_id}>
                {payer.payer_name}{payer.billing_name && payer.billing_name !== payer.payer_name
                  ? ` · se factura a ${payer.billing_name}` : ''}
              </option>
            ))}
          </select>
        </Field>
        {mutation.error && <Note tone="danger">{mutation.error.message}</Note>}
      </div>
      <div className="modal__actions">
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="primary" disabled={!payerId || mutation.isPending} onClick={() => mutation.mutate()}>
          {mutation.isPending ? 'Vinculando…' : 'Confirmar vínculo'}
        </Button>
      </div>
    </Modal>
  );
}

/**
 * Alta o complemento manual de una factura. La primera llamada solo analiza;
 * la segunda, explícita, persiste. Cambiar cualquier campo invalida la vista
 * previa para que nunca se confirme algo distinto de lo que se revisó.
 */
function ManualInvoiceModal({ unitId, unitCode, month, invoice, onClose }: {
  unitId: string; unitCode: string; month: InvoiceYearMonth;
  invoice: InvoiceYearDocument | null; onClose: () => void;
}) {
  const initialPayer = invoice?.charge_payer_id
    ?? (month.payers.length === 1 ? month.payers[0]!.charge_payer_id : '');
  const payerOf = (id: string) => month.payers.find((payer) => payer.charge_payer_id === id) ?? null;
  const initial = payerOf(initialPayer);
  const [payerId, setPayerId] = useState(initialPayer);
  const [pdf, setPdf] = useState<File | null>(null);
  const [xml, setXml] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [documentNumber, setDocumentNumber] = useState(invoice?.document_number ?? '');
  const [documentType, setDocumentType] = useState(invoice?.document_type ?? 'Factura Electrónica');
  const [issuedOn, setIssuedOn] = useState(invoice?.issued_on?.slice(0, 10) ?? today());
  const [receptorName, setReceptorName] = useState(invoice?.receptor_name ?? initial?.billing_name ?? initial?.payer_name ?? '');
  const [receptorTaxId, setReceptorTaxId] = useState(invoice?.receptor_tax_id ?? initial?.billing_tax_id ?? '');
  const [total, setTotal] = useState(minorToColonDigits(invoice?.total_minor ?? initial?.due_minor ?? month.due_minor));
  const [periods, setPeriods] = useState(month.period.slice(0, 7));

  const form = () => {
    const data = new FormData();
    data.set('charge_payer_id', payerId);
    if (invoice) data.set('invoice_id', invoice.id);
    if (pdf) data.set('pdf', pdf, pdf.name);
    if (xml) data.set('xml', xml, xml.name);
    data.set('document_number', documentNumber);
    data.set('document_type', documentType);
    data.set('issued_on', issuedOn);
    data.set('receptor_name', receptorName);
    data.set('receptor_tax_id', receptorTaxId);
    data.set('total_minor', String(colonDigitsToMinor(total) ?? ''));
    data.set('periods', JSON.stringify(periods.split(',').map((value) => value.trim()).filter(Boolean)));
    return data;
  };

  const preview = useApiMutation(
    () => api.post<ManualInvoicePreview>('/admin/manual-invoices/preview', form()),
    [],
    {
      onSuccess: (data) => {
        setDocumentNumber(data.analysis.document_number);
        setDocumentType(data.analysis.document_type);
        setIssuedOn(data.analysis.issued_on.slice(0, 10));
        setReceptorName(data.analysis.receptor_name ?? '');
        setReceptorTaxId(data.analysis.receptor_tax_id ?? '');
        setTotal(minorToColonDigits(data.analysis.total_minor));
        setPeriods(data.periods.map((value) => value.slice(0, 7)).join(', '));
      },
    },
  );
  const save = useApiMutation(
    () => api.post('/admin/manual-invoices', form()),
    [`/admin/units/${unitId}/invoice-year`, `/admin/units/${unitId}`, '/admin/invoices'],
    { onSuccess: onClose },
  );

  const changed = () => preview.reset();
  const takeFiles = (files: FileList | File[]) => {
    setFileError(null);
    for (const file of Array.from(files)) {
      const name = file.name.toLowerCase();
      if (file.type === 'application/pdf' || name.endsWith('.pdf')) setPdf(file);
      else if (file.type === 'application/xml' || file.type === 'text/xml' || name.endsWith('.xml')) setXml(file);
      else setFileError(`«${file.name}» no es PDF ni XML.`);
    }
    changed();
  };
  const dropped = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    takeFiles(event.dataTransfer.files);
  };
  const selectedPayer = payerOf(payerId);
  const missingPdf = invoice?.has_pdf === false;
  const missingXml = invoice?.has_xml === false;
  const hasNewFile = Boolean(pdf || xml);

  return (
    <Modal
      title={`${invoice ? 'Completar' : 'Registrar'} factura · ${unitCode} · ${periodLabel(month.period)}`}
      wide
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancelar</Button>
          {!preview.data ? (
            <Button variant="primary" disabled={!payerId || !hasNewFile || preview.isPending}
              onClick={() => preview.mutate()}>
              {preview.isPending ? 'Analizando…' : 'Revisar factura'}
            </Button>
          ) : (
            <Button variant="primary" disabled={save.isPending} onClick={() => save.mutate()}>
              {save.isPending ? 'Guardando…' : 'Confirmar y guardar'}
            </Button>
          )}
        </>
      }
    >
      {(fileError || preview.error || save.error) && (
        <Note tone="danger">{fileError ?? preview.error?.message ?? save.error?.message}</Note>
      )}

      {month.payers.length > 1 && (
        <Field label="Cuenta por cobrar" required hint="Elegí a quién corresponde; el sistema no adivina entre varios pagadores.">
          <select className="select" value={payerId} onChange={(event) => {
            const next = payerOf(event.target.value);
            setPayerId(event.target.value);
            setReceptorName(next?.billing_name ?? next?.payer_name ?? '');
            setReceptorTaxId(next?.billing_tax_id ?? '');
            setTotal(minorToColonDigits(next?.due_minor ?? month.due_minor));
            changed();
          }}>
            <option value="">Elegir responsable…</option>
            {month.payers.map((payer) => (
              <option key={payer.charge_payer_id} value={payer.charge_payer_id}>
                {payer.payer_name} · {payer.billing_name ?? 'sin receptor fiscal'} · {formatColonDigits(minorToColonDigits(payer.due_minor))}
              </option>
            ))}
          </select>
        </Field>
      )}

      <div className="invoice-upload" onDragOver={(event) => event.preventDefault()} onDrop={dropped}>
        <Icon name="documento" />
        <div>
          <strong>Arrastrá el PDF, el XML o ambos</strong>
          <span className="tbl__sub">
            {invoice ? `Ya existe: ${invoice.has_pdf ? 'PDF' : 'sin PDF'} · ${invoice.has_xml ? 'XML' : 'sin XML'}.` : 'Los originales se guardan por separado.'}
          </span>
        </div>
        <div className="row row--wrap">
          <label className="btn">
            Elegir PDF<input className="sr-only" type="file" accept="application/pdf,.pdf"
              onChange={(event) => event.target.files && takeFiles(event.target.files)} />
          </label>
          <label className="btn">
            Elegir XML<input className="sr-only" type="file" accept="application/xml,text/xml,.xml"
              onChange={(event) => event.target.files && takeFiles(event.target.files)} />
          </label>
        </div>
        {(pdf || xml) && (
          <div className="small">
            {pdf && <span>PDF: {pdf.name}</span>}{pdf && xml && ' · '}{xml && <span>XML: {xml.name}</span>}
          </div>
        )}
        {invoice && !hasNewFile && (
          <span className="small muted">Adjuntá {missingPdf && missingXml ? 'el archivo faltante' : missingPdf ? 'el PDF faltante' : 'el XML faltante'}.</span>
        )}
      </div>

      <div className="grid grid--2 mt-2">
        <Field label="Número de comprobante" required>
          <input className="input" inputMode="numeric" value={documentNumber}
            onChange={(event) => { setDocumentNumber(event.target.value.replace(/\D/g, '').slice(0, 20)); changed(); }} />
        </Field>
        <Field label="Tipo" required>
          <select className="select" value={documentType}
            onChange={(event) => { setDocumentType(event.target.value); changed(); }}>
            <option>Factura Electrónica</option><option>Tiquete Electrónico</option>
            <option>Nota de Crédito</option><option>Nota de Débito</option>
          </select>
        </Field>
        <Field label="Fecha de emisión" required>
          <input className="input" type="date" value={issuedOn}
            onChange={(event) => { setIssuedOn(event.target.value); changed(); }} />
        </Field>
        <Field label="Total (colones)" required>
          <input className="input input--money" inputMode="numeric" value={formatColonDigits(total)}
            onChange={(event) => { setTotal(colonDigits(event.target.value)); changed(); }} />
        </Field>
        <Field label="Receptor fiscal" required>
          <input className="input" value={receptorName}
            onChange={(event) => { setReceptorName(event.target.value); changed(); }} />
        </Field>
        <Field label="Cédula receptora" required>
          <input className="input" value={receptorTaxId}
            onChange={(event) => { setReceptorTaxId(event.target.value); changed(); }} />
        </Field>
      </div>
      <Field label="Meses cubiertos" required hint="Uno o varios meses en formato AAAA-MM, separados por coma.">
        <input className="input" value={periods}
          onChange={(event) => { setPeriods(event.target.value); changed(); }} />
      </Field>

      {preview.data && (
        <div className="invoice-preview mt-2" role="status">
          <div className="row row--wrap">
            <Badge tone={preview.data.analysis.review_reasons.length > 0 ? 'warn' : 'ok'}>
              {preview.data.analysis.review_reasons.length > 0 ? 'Se guardará para revisión' : 'Datos coherentes'}
            </Badge>
            {preview.data.analysis.signature_present === true && <Badge tone="info">Firma presente</Badge>}
            {preview.data.analysis.signature_present === null && <Badge tone="neutral">Sin XML</Badge>}
          </div>
          <dl className="dl">
            <dt>Documento</dt><dd>{preview.data.analysis.document_number}</dd>
            <dt>Receptor</dt><dd>{preview.data.analysis.receptor_name} · {preview.data.analysis.receptor_tax_id}</dd>
            <dt>Total</dt><dd><Money value={preview.data.analysis.total_minor} /></dd>
            <dt>Meses</dt><dd>{preview.data.periods.map(periodLabelShort).join(', ') || 'No aplica (nota)'}</dd>
          </dl>
          {preview.data.analysis.review_reasons.length > 0 && (
            <Note tone="warn">{preview.data.analysis.review_reasons.join(' · ')}</Note>
          )}
          {preview.data.analysis.signature_present === true && (
            <p className="small muted">Se detectó la firma en el XML; no se realizó validación criptográfica ni consulta a Hacienda.</p>
          )}
        </div>
      )}
      {selectedPayer && (
        <p className="small muted mt-2">
          Pago actual: <Money value={selectedPayer.paid_minor} /> de <Money value={selectedPayer.due_minor} />. Guardar la factura no cambia estos valores.
        </p>
      )}
    </Modal>
  );
}

/**
 * El comprobante abierto, del lado del administrador.
 *
 * El documento lo pinta `InvoiceViewer`, que es compartido con el portal. La
 * presentación prioriza el PDF original; si falta, usa XML o metadatos.
 *
 * Se imprime con el botón. `app.css` ya aísla el modal en `@media print`;
 * `invoice-doc.css` agrega el `@page` que saca el encabezado y el pie que
 * Chrome mete dentro del papel —la hora y la URL del admin— porque esto se le
 * manda a un cliente.
 *
 * El PDF y el XML se ofrecen por separado porque cumplen funciones distintas:
 * el primero conserva el diseño de GTI y el segundo es el comprobante firmado.
 */
export function InvoiceModal({ invoice, onClose }: { invoice: InvoiceViewTarget; onClose: () => void }) {
  const query = useApi<InvoiceDetail>(`/admin/invoices/${invoice.id}/detail`);

  return (
    <Modal
      title={`${isCreditNote(invoice.document_type) ? 'Nota de crédito' : 'Factura'} ${invoice.document_number}`}
      wide
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cerrar</Button>
          {invoice.has_xml && (
            <a className="btn" href={`/api/admin/invoices/${invoice.id}/xml`}>Descargar XML</a>
          )}
          {invoice.has_pdf ? (
            <a className="btn btn--primary" target="_blank" rel="noreferrer"
              href={`/api/admin/invoices/${invoice.id}/pdf`}>Abrir o guardar PDF</a>
          ) : (
            <Button variant="primary" onClick={() => window.print()}>Imprimir o guardar PDF</Button>
          )}
        </>
      }
    >
      <Async query={query}>{(d) => (
        <div className="invoice-view-stack">
          {d.presentation?.mode === 'XML_RENDER' && (
            <Note tone="info">Representación gráfica generada desde el XML fiscal firmado. No es el PDF original del proveedor.</Note>
          )}
          {d.presentation?.mode === 'METADATA_RENDER' && (
            <Note tone="warn">Solo hay datos registrados. Esta vista no sustituye el XML fiscal ni un PDF original.</Note>
          )}
          <InvoiceViewer
            documentType={invoice.document_type}
            receptorName={invoice.receptor_name}
            d={d}
            pdfUrl={`/api/admin/invoices/${invoice.id}/pdf`}
          />
        </div>
      )}</Async>
    </Modal>
  );
}

